import { RoutesProvider } from '@folklore/routes';
import { TrackingContainer } from '@folklore/tracking';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { Router } from 'wouter';

import * as AppPropTypes from '../lib/PropTypes';
import Tracking from '../lib/Tracking';

import { ApiProvider } from '../contexts/ApiContext';
import { KeysProvider } from '../contexts/KeysContext';
import { SiteProvider } from '../contexts/SiteContext';
import { ToastProvider } from '../contexts/ToastContext';
import { UserProvider } from '../contexts/UserContext';
import Routes from './Routes';

const propTypes = {
    intl: PropTypes.shape({
        locale: PropTypes.string,
        messages: PropTypes.oneOfType([
            PropTypes.objectOf(PropTypes.objectOf(PropTypes.string)),
            PropTypes.objectOf(PropTypes.string),
        ]),
    }),
    routes: PropTypes.objectOf(PropTypes.string),
    user: AppPropTypes.user,
    favorites: PropTypes.arrayOf(PropTypes.string),
    page: AppPropTypes.page,
    site: PropTypes.shape({}),
    sites: PropTypes.arrayOf(PropTypes.shape({})),
    edition: PropTypes.shape({}),
    statusCode: PropTypes.number,
    menus: PropTypes.shape({
        main: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    catalog: PropTypes.shape({ id: PropTypes.string }),
    showConsent: PropTypes.bool,
    keys: PropTypes.shape({ stripe: PropTypes.string }),
};

function App({
    intl = null,
    routes = {},
    user = null,
    favorites = null,
    page = null,
    site = null,
    sites = [],
    edition = null,
    statusCode = null,
    menus = null,
    catalog = null,
    showConsent = null,
    keys = null,
}) {
    const { locale = null, messages = null } = intl || {};
    const tracking = useMemo(() => new Tracking(), []);

    const queryClient = useMemo(() => {
        const client = new QueryClient({
            defaultOptions: {
                queries: {
                    staleTime: Infinity,
                },
            },
        });
        if (page !== null) {
            const query =
                window && !isEmpty(window.location.search)
                    ? queryString.parse(window.location.search, {
                          arrayFormat: 'bracket',
                      })
                    : null;
            client.setQueryData(['pages', page.url, query], page);
        }
        return client;
    }, [page]);

    return (
        <Router hook={null}>
            <IntlProvider locale={locale} messages={messages[locale] || messages}>
                <KeysProvider keys={keys}>
                    <RoutesProvider routes={routes}>
                        <ApiProvider>
                            <UserProvider user={user} favorites={favorites}>
                                <QueryClientProvider client={queryClient}>
                                    {/* <ReactQueryDevtools /> */}
                                    <TrackingContainer tracking={tracking}>
                                        <ToastProvider>
                                            <SiteProvider
                                                site={site}
                                                sites={sites}
                                                edition={edition}
                                                menus={menus}
                                                showCookies={showConsent}
                                                catalog={catalog}
                                            >
                                                <Routes
                                                    routes={routes}
                                                    page={page}
                                                    statusCode={statusCode}
                                                />
                                            </SiteProvider>
                                        </ToastProvider>
                                    </TrackingContainer>
                                </QueryClientProvider>
                            </UserProvider>
                        </ApiProvider>
                    </RoutesProvider>
                </KeysProvider>
            </IntlProvider>
        </Router>
    );
}

App.propTypes = propTypes;

export default App;
